/**
 * Performance Alerting Service
 * 
 * Real-time performance degradation detection and alerting system.
 * Monitors key metrics and triggers alerts when thresholds are exceeded.
 * 
 * Features:
 * - Real-time metric monitoring with configurable thresholds
 * - Multi-level alert severity (INFO, WARNING, CRITICAL, EMERGENCY)
 * - Alert aggregation and deduplication to prevent spam
 * - Escalation policies and automatic recovery detection
 * - Integration with external notification systems
 * - Performance trend analysis and predictive alerting
 * - Alert fatigue prevention and intelligent throttling
 */

import { CategoryLogger, LogLevel } from './category-loader';
import { PerformanceMonitor, PerformanceStats, ResourceMetrics } from './performance-monitor';
import { processingOptimizer } from './processing-optimizer';

// Get performance monitor singleton
const performanceMonitor = PerformanceMonitor.getInstance();

/**
 * Alert severity levels
 */
export enum AlertSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING', 
  CRITICAL = 'CRITICAL',
  EMERGENCY = 'EMERGENCY'
}

/**
 * Alert categories for classification
 */
export enum AlertCategory {
  PERFORMANCE = 'PERFORMANCE',
  AVAILABILITY = 'AVAILABILITY',
  CAPACITY = 'CAPACITY',
  QUALITY = 'QUALITY',
  SECURITY = 'SECURITY'
}

/**
 * Alert trigger conditions
 */
export enum AlertTrigger {
  THRESHOLD_EXCEEDED = 'THRESHOLD_EXCEEDED',
  TREND_DEGRADATION = 'TREND_DEGRADATION',
  ANOMALY_DETECTED = 'ANOMALY_DETECTED',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  CIRCUIT_BREAKER_OPEN = 'CIRCUIT_BREAKER_OPEN'
}

/**
 * Alert information
 */
export interface PerformanceAlert {
  id: string;
  timestamp: number;
  severity: AlertSeverity;
  category: AlertCategory;
  trigger: AlertTrigger;
  title: string;
  description: string;
  metric: string;
  currentValue: number;
  thresholdValue: number;
  context: Record<string, any>;
  acknowledged: boolean;
  resolved: boolean;
  resolvedAt?: number;
  acknowledgedBy?: string;
  escalated: boolean;
  escalatedAt?: number;
  correlationId?: string;
}

/**
 * Alert threshold configuration
 */
export interface AlertThreshold {
  metric: string;
  severity: AlertSeverity;
  operator: 'greater_than' | 'less_than' | 'equals' | 'not_equals';
  value: number;
  duration?: number; // Sustained duration in ms before triggering
  enabled: boolean;
  description: string;
}

/**
 * Alert notification handler interface
 */
export interface AlertNotificationHandler {
  name: string;
  enabled: boolean;
  handleAlert(alert: PerformanceAlert): Promise<void>;
  handleResolution(alert: PerformanceAlert): Promise<void>;
}

/**
 * Alert aggregation rule
 */
export interface AlertAggregationRule {
  pattern: string; // Regex pattern for alert matching
  timeWindow: number; // Time window in ms for aggregation
  maxAlerts: number; // Maximum alerts before aggregating
  suppressionDuration: number; // How long to suppress in ms
}

/**
 * Alert trend analysis result
 */
export interface TrendAnalysis {
  metric: string;
  trend: 'improving' | 'degrading' | 'stable';
  rate: number; // Rate of change per minute
  confidence: number; // 0-1 confidence score
  prediction: {
    nextValue: number;
    timeToThreshold?: number; // ms until threshold breach
  };
}

/**
 * Performance alerting configuration
 */
export interface PerformanceAlertingConfig {
  enabled: boolean;
  checkInterval: number; // Check interval in ms
  alertRetention: number; // How long to keep alerts in ms
  maxActiveAlerts: number;
  enableTrendAnalysis: boolean;
  enablePredictiveAlerts: boolean;
  aggregationRules: AlertAggregationRule[];
  notificationHandlers: AlertNotificationHandler[];
  thresholds: AlertThreshold[];
}

/**
 * Default performance alerting configuration
 */
const DEFAULT_CONFIG: PerformanceAlertingConfig = {
  enabled: true,
  checkInterval: 30000, // 30 seconds
  alertRetention: 24 * 60 * 60 * 1000, // 24 hours
  maxActiveAlerts: 1000,
  enableTrendAnalysis: true,
  enablePredictiveAlerts: true,
  aggregationRules: [
    {
      pattern: 'classification_timeout.*',
      timeWindow: 5 * 60 * 1000, // 5 minutes
      maxAlerts: 3,
      suppressionDuration: 10 * 60 * 1000 // 10 minutes
    },
    {
      pattern: 'high_latency.*',
      timeWindow: 2 * 60 * 1000, // 2 minutes
      maxAlerts: 5,
      suppressionDuration: 5 * 60 * 1000 // 5 minutes
    }
  ],
  notificationHandlers: [],
  thresholds: [
    // Response time thresholds
    {
      metric: 'averageResponseTime',
      severity: AlertSeverity.WARNING,
      operator: 'greater_than',
      value: 5000, // 5 seconds
      duration: 60000, // 1 minute sustained
      enabled: true,
      description: 'Classification response time elevated'
    },
    {
      metric: 'averageResponseTime',
      severity: AlertSeverity.CRITICAL,
      operator: 'greater_than',
      value: 10000, // 10 seconds
      duration: 30000, // 30 seconds sustained
      enabled: true,
      description: 'Classification response time critically high'
    },
    // Error rate thresholds
    {
      metric: 'errorRate',
      severity: AlertSeverity.WARNING,
      operator: 'greater_than',
      value: 0.05, // 5%
      duration: 120000, // 2 minutes sustained
      enabled: true,
      description: 'Classification error rate elevated'
    },
    {
      metric: 'errorRate',
      severity: AlertSeverity.CRITICAL,
      operator: 'greater_than',
      value: 0.15, // 15%
      duration: 60000, // 1 minute sustained
      enabled: true,
      description: 'Classification error rate critically high'
    },
    // Throughput thresholds
    {
      metric: 'throughput',
      severity: AlertSeverity.WARNING,
      operator: 'less_than',
      value: 10, // 10 requests per minute
      duration: 300000, // 5 minutes sustained
      enabled: true,
      description: 'Classification throughput degraded'
    },
    // Memory usage thresholds
    {
      metric: 'memoryUsage',
      severity: AlertSeverity.WARNING,
      operator: 'greater_than',
      value: 0.8, // 80%
      duration: 180000, // 3 minutes sustained
      enabled: true,
      description: 'High memory usage detected'
    },
    {
      metric: 'memoryUsage',
      severity: AlertSeverity.CRITICAL,
      operator: 'greater_than',
      value: 0.9, // 90%
      duration: 60000, // 1 minute sustained
      enabled: true,
      description: 'Critical memory usage detected'
    }
  ]
};

/**
 * Performance Alerting Service
 * 
 * Monitors performance metrics and triggers alerts when degradation is detected
 */
class PerformanceAlertingService {
  private readonly logger = CategoryLogger.getInstance();
  private readonly config: PerformanceAlertingConfig;
  
  private activeAlerts = new Map<string, PerformanceAlert>();
  private alertHistory: PerformanceAlert[] = [];
  private suppressedAlerts = new Map<string, number>(); // Pattern -> suppression end time
  private sustainedThresholds = new Map<string, number>(); // Threshold ID -> start time
  private trendHistory = new Map<string, number[]>(); // Metric -> values
  
  private monitoringInterval?: NodeJS.Timeout;
  private cleanupInterval?: NodeJS.Timeout;
  private isMonitoring = false;

  constructor(config: Partial<PerformanceAlertingConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    
    this.logger.debug('Performance alerting service initialized', 'constructor', {
      enabled: this.config.enabled,
      checkInterval: this.config.checkInterval,
      thresholdCount: this.config.thresholds.length,
      handlerCount: this.config.notificationHandlers.length
    });
  }

  /**
   * Start performance monitoring and alerting
   */
  public startMonitoring(): void {
    if (this.isMonitoring) {
      this.logger.warn('Performance monitoring already running', 'startMonitoring');
      return;
    }

    if (!this.config.enabled) {
      this.logger.info('Performance alerting disabled by configuration', 'startMonitoring');
      return;
    }

    this.isMonitoring = true;

    // Start metric monitoring
    this.monitoringInterval = setInterval(() => {
      this.checkPerformanceMetrics().catch(error => {
        this.logger.error('Error during performance monitoring', 'startMonitoring', {
          error: error instanceof Error ? error.message : String(error)
        });
      });
    }, this.config.checkInterval);

    // Start periodic cleanup
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredData();
    }, 60000); // Cleanup every minute

    this.logger.info('Performance monitoring started', 'startMonitoring', {
      checkInterval: this.config.checkInterval,
      thresholdCount: this.config.thresholds.length
    });
  }

  /**
   * Stop performance monitoring
   */
  public stopMonitoring(): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;

    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = undefined;
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = undefined;
    }

    this.logger.info('Performance monitoring stopped', 'stopMonitoring');
  }

  /**
   * Check current performance metrics against thresholds
   */
  private async checkPerformanceMetrics(): Promise<void> {
    try {
      const stats = performanceMonitor.getStats();
      const resourceMetricsArray = performanceMonitor.getResourceMetrics();
      const resourceMetrics = resourceMetricsArray[resourceMetricsArray.length - 1] || {
        memoryUsageMB: 0,
        cpuUsagePercent: 0,
        activeConnections: 0,
        queueDepth: 0,
        timestamp: Date.now()
      };
      const optimizerStatus = processingOptimizer.getOptimizerStatus();

      // Update trend history
      this.updateTrendHistory(stats, resourceMetrics);

      // Check all enabled thresholds
      for (const threshold of this.config.thresholds.filter(t => t.enabled)) {
        await this.checkThreshold(threshold, stats, resourceMetrics, optimizerStatus);
      }

      // Perform trend analysis if enabled
      if (this.config.enableTrendAnalysis) {
        await this.performTrendAnalysis();
      }

      // Check for circuit breaker alerts
      this.checkCircuitBreakerAlerts(optimizerStatus);

    } catch (error) {
      this.logger.error('Error checking performance metrics', 'checkPerformanceMetrics', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  /**
   * Check a specific threshold against current metrics
   */
  private async checkThreshold(
    threshold: AlertThreshold,
    stats: PerformanceStats,
    resourceMetrics: ResourceMetrics,
    optimizerStatus: any
  ): Promise<void> {
    const currentValue = this.getMetricValue(threshold.metric, stats, resourceMetrics, optimizerStatus);
    
    if (currentValue === undefined) {
      return; // Metric not available
    }

    const thresholdKey = `${threshold.metric}_${threshold.severity}_${threshold.value}`;
    const now = Date.now();
    const isBreached = this.evaluateThreshold(threshold, currentValue);

    if (isBreached) {
      // Check if we need to wait for sustained duration
      if (threshold.duration) {
        const startTime = this.sustainedThresholds.get(thresholdKey);
        if (!startTime) {
          // Start tracking sustained breach
          this.sustainedThresholds.set(thresholdKey, now);
          return;
        } else if (now - startTime < threshold.duration) {
          // Still within duration, wait longer
          return;
        }
      }

      // Threshold breached, create alert
      await this.createAlert({
        severity: threshold.severity,
        category: this.getAlertCategory(threshold.metric),
        trigger: AlertTrigger.THRESHOLD_EXCEEDED,
        title: threshold.description,
        description: `${threshold.metric} ${threshold.operator.replace('_', ' ')} ${threshold.value} (current: ${currentValue.toFixed(2)})`,
        metric: threshold.metric,
        currentValue,
        thresholdValue: threshold.value,
        context: {
          thresholdConfig: threshold,
          stats,
          resourceMetrics
        }
      });

    } else {
      // Threshold not breached, clear sustained tracking
      this.sustainedThresholds.delete(thresholdKey);
      
      // Check for auto-resolution
      await this.checkAutoResolution(threshold.metric, currentValue);
    }
  }

  /**
   * Get metric value from performance data
   */
  private getMetricValue(
    metric: string,
    stats: PerformanceStats,
    resourceMetrics: ResourceMetrics,
    optimizerStatus: any
  ): number | undefined {
    // Map metric names to actual values
    switch (metric) {
      case 'averageResponseTime':
        return stats.averageDuration;
      case 'errorRate':
        return stats.totalOperations > 0 ? stats.failedOperations / stats.totalOperations : 0;
      case 'throughput':
        return stats.operationsPerSecond * 60; // Convert to per minute
      case 'memoryUsage':
        return resourceMetrics.memoryUsageMB;
      case 'successRate':
        return stats.successRate;
      case 'queueDepth':
        return resourceMetrics.queueDepth || 0;
      case 'concurrentRequests':
        return resourceMetrics.activeConnections || 0;
      default:
        return undefined;
    }
  }

  /**
   * Evaluate if threshold is breached
   */
  private evaluateThreshold(threshold: AlertThreshold, currentValue: number): boolean {
    switch (threshold.operator) {
      case 'greater_than':
        return currentValue > threshold.value;
      case 'less_than':
        return currentValue < threshold.value;
      case 'equals':
        return Math.abs(currentValue - threshold.value) < 0.001;
      case 'not_equals':
        return Math.abs(currentValue - threshold.value) >= 0.001;
      default:
        return false;
    }
  }

  /**
   * Get alert category for a metric
   */
  private getAlertCategory(metric: string): AlertCategory {
    if (metric.includes('response') || metric.includes('latency')) {
      return AlertCategory.PERFORMANCE;
    } else if (metric.includes('error') || metric.includes('success')) {
      return AlertCategory.AVAILABILITY;
    } else if (metric.includes('memory') || metric.includes('queue') || metric.includes('throughput')) {
      return AlertCategory.CAPACITY;
    } else {
      return AlertCategory.QUALITY;
    }
  }

  /**
   * Create a new alert
   */
  private async createAlert(alertData: Partial<PerformanceAlert>): Promise<void> {
    const alertId = this.generateAlertId(alertData.metric!, alertData.severity!);
    
    // Check if alert already exists
    if (this.activeAlerts.has(alertId)) {
      return; // Avoid duplicate alerts
    }

    // Check aggregation rules
    if (this.shouldSuppressAlert(alertData)) {
      return;
    }

    const alert: PerformanceAlert = {
      id: alertId,
      timestamp: Date.now(),
      severity: alertData.severity!,
      category: alertData.category!,
      trigger: alertData.trigger!,
      title: alertData.title!,
      description: alertData.description!,
      metric: alertData.metric!,
      currentValue: alertData.currentValue!,
      thresholdValue: alertData.thresholdValue!,
      context: alertData.context || {},
      acknowledged: false,
      resolved: false,
      escalated: false
    };

    // Add to active alerts
    this.activeAlerts.set(alertId, alert);
    this.alertHistory.push(alert);

    // Notify handlers
    await this.notifyHandlers(alert);

    this.logger.warn('Performance alert created', 'createAlert', {
      alertId: alert.id,
      severity: alert.severity,
      metric: alert.metric,
      currentValue: alert.currentValue,
      thresholdValue: alert.thresholdValue
    });
  }

  /**
   * Check if alert should be suppressed based on aggregation rules
   */
  private shouldSuppressAlert(alertData: Partial<PerformanceAlert>): boolean {
    const now = Date.now();
    const alertKey = `${alertData.metric}_${alertData.severity}`;

    // Check suppression
    const suppressionEnd = this.suppressedAlerts.get(alertKey);
    if (suppressionEnd && now < suppressionEnd) {
      return true;
    }

    // Check aggregation rules
    for (const rule of this.config.aggregationRules) {
      const regex = new RegExp(rule.pattern);
      if (regex.test(alertKey)) {
        const recentAlerts = this.alertHistory.filter(alert => 
          regex.test(`${alert.metric}_${alert.severity}`) &&
          now - alert.timestamp < rule.timeWindow
        );

        if (recentAlerts.length >= rule.maxAlerts) {
          // Trigger suppression
          this.suppressedAlerts.set(alertKey, now + rule.suppressionDuration);
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Generate unique alert ID
   */
  private generateAlertId(metric: string, severity: AlertSeverity): string {
    return `${metric}_${severity}_${Date.now()}`;
  }

  /**
   * Notify alert handlers
   */
  private async notifyHandlers(alert: PerformanceAlert): Promise<void> {
    const enabledHandlers = this.config.notificationHandlers.filter(h => h.enabled);
    
    const notificationPromises = enabledHandlers.map(async handler => {
      try {
        await handler.handleAlert(alert);
      } catch (error) {
        this.logger.error('Alert handler failed', 'notifyHandlers', {
          handlerName: handler.name,
          alertId: alert.id,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    });

    await Promise.allSettled(notificationPromises);
  }

  /**
   * Check for auto-resolution of alerts
   */
  private async checkAutoResolution(metric: string, currentValue: number): Promise<void> {
    const alertsToResolve = Array.from(this.activeAlerts.values()).filter(alert => 
      alert.metric === metric && !alert.resolved
    );

    for (const alert of alertsToResolve) {
      // Check if conditions have improved sufficiently
      const improvement = this.calculateImprovement(alert, currentValue);
      if (improvement > 0.1) { // 10% improvement threshold
        await this.resolveAlert(alert.id, 'auto-resolved');
      }
    }
  }

  /**
   * Calculate improvement percentage
   */
  private calculateImprovement(alert: PerformanceAlert, currentValue: number): number {
    const threshold = alert.thresholdValue;
    const alertValue = alert.currentValue;
    
    if (alert.severity === AlertSeverity.WARNING || alert.severity === AlertSeverity.CRITICAL) {
      // For high-is-bad metrics
      if (alertValue > threshold) {
        return Math.max(0, (alertValue - currentValue) / (alertValue - threshold));
      }
      // For low-is-bad metrics
      else {
        return Math.max(0, (currentValue - alertValue) / (threshold - alertValue));
      }
    }
    
    return 0;
  }

  /**
   * Resolve an alert
   */
  public async resolveAlert(alertId: string, resolvedBy: string = 'system'): Promise<void> {
    const alert = this.activeAlerts.get(alertId);
    if (!alert || alert.resolved) {
      return;
    }

    alert.resolved = true;
    alert.resolvedAt = Date.now();

    // Notify handlers of resolution
    const enabledHandlers = this.config.notificationHandlers.filter(h => h.enabled);
    const resolutionPromises = enabledHandlers.map(async handler => {
      try {
        await handler.handleResolution(alert);
      } catch (error) {
        this.logger.error('Resolution handler failed', 'resolveAlert', {
          handlerName: handler.name,
          alertId: alert.id,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    });

    await Promise.allSettled(resolutionPromises);

    this.logger.info('Alert resolved', 'resolveAlert', {
      alertId: alert.id,
      resolvedBy,
      duration: alert.resolvedAt - alert.timestamp
    });
  }

  /**
   * Acknowledge an alert
   */
  public acknowledgeAlert(alertId: string, acknowledgedBy: string): void {
    const alert = this.activeAlerts.get(alertId);
    if (!alert || alert.acknowledged) {
      return;
    }

    alert.acknowledged = true;
    alert.acknowledgedBy = acknowledgedBy;

    this.logger.info('Alert acknowledged', 'acknowledgeAlert', {
      alertId: alert.id,
      acknowledgedBy
    });
  }

  /**
   * Update trend history for metrics
   */
  private updateTrendHistory(stats: PerformanceStats, resourceMetrics: ResourceMetrics): void {
    const metrics = {
      averageResponseTime: stats.averageDuration,
      errorRate: stats.totalOperations > 0 ? stats.failedOperations / stats.totalOperations : 0,
      throughput: stats.operationsPerSecond * 60, // Convert to per minute
      memoryUsage: resourceMetrics.memoryUsageMB
    };

    const maxHistoryLength = 60; // Keep 60 data points (30 minutes at 30s intervals)

    for (const [metric, value] of Object.entries(metrics)) {
      if (!this.trendHistory.has(metric)) {
        this.trendHistory.set(metric, []);
      }
      
      const history = this.trendHistory.get(metric)!;
      history.push(value);
      
      // Limit history size
      if (history.length > maxHistoryLength) {
        history.shift();
      }
    }
  }

  /**
   * Perform trend analysis and predictive alerting
   */
  private async performTrendAnalysis(): Promise<void> {
    if (!this.config.enablePredictiveAlerts) {
      return;
    }

    for (const [metric, values] of this.trendHistory.entries()) {
      if (values.length < 10) {
        continue; // Need at least 10 data points
      }

      const analysis = this.analyzeTrend(metric, values);
      
      // Check if trend predicts threshold breach
      if (analysis.trend === 'degrading' && analysis.prediction.timeToThreshold) {
        const timeToThreshold = analysis.prediction.timeToThreshold;
        
        // Alert if threshold breach predicted within next 10 minutes
        if (timeToThreshold < 10 * 60 * 1000) {
          await this.createAlert({
            severity: AlertSeverity.WARNING,
            category: AlertCategory.PERFORMANCE,
            trigger: AlertTrigger.TREND_DEGRADATION,
            title: `Predictive Alert: ${metric} degradation detected`,
            description: `Trend analysis predicts ${metric} will breach threshold in ${Math.round(timeToThreshold / 60000)} minutes`,
            metric,
            currentValue: values[values.length - 1],
            thresholdValue: analysis.prediction.nextValue,
            context: {
              trendAnalysis: analysis,
              predictedBreachTime: Date.now() + timeToThreshold
            }
          });
        }
      }
    }
  }

  /**
   * Analyze trend for a metric
   */
  private analyzeTrend(metric: string, values: number[]): TrendAnalysis {
    const recentValues = values.slice(-10); // Last 10 values
    const earlierValues = values.slice(-20, -10); // Previous 10 values
    
    const recentAvg = recentValues.reduce((a, b) => a + b, 0) / recentValues.length;
    const earlierAvg = earlierValues.reduce((a, b) => a + b, 0) / earlierValues.length;
    
    const change = recentAvg - earlierAvg;
    const changePercent = earlierAvg > 0 ? change / earlierAvg : 0;
    
    // Calculate rate per minute (assuming 30s intervals)
    const ratePerMinute = change * 2; // 2 intervals per minute
    
    // Determine trend direction
    let trend: 'improving' | 'degrading' | 'stable';
    if (Math.abs(changePercent) < 0.05) { // Less than 5% change
      trend = 'stable';
    } else if (this.isHigherBad(metric)) {
      trend = change > 0 ? 'degrading' : 'improving';
    } else {
      trend = change > 0 ? 'improving' : 'degrading';
    }
    
    // Calculate confidence based on variance
    const variance = this.calculateVariance(recentValues);
    const confidence = Math.max(0, Math.min(1, 1 - (variance / recentAvg)));
    
    // Predict next value
    const nextValue = recentAvg + ratePerMinute;
    
    // Calculate time to threshold if degrading
    let timeToThreshold: number | undefined;
    if (trend === 'degrading' && ratePerMinute > 0) {
      const relevantThresholds = this.config.thresholds.filter(t => 
        t.metric === metric && t.enabled
      );
      
      for (const threshold of relevantThresholds) {
        if (this.isHigherBad(metric) && nextValue < threshold.value) {
          const timeToReach = Math.abs((threshold.value - recentAvg) / ratePerMinute) * 60000;
          if (!timeToThreshold || timeToReach < timeToThreshold) {
            timeToThreshold = timeToReach;
          }
        }
      }
    }
    
    return {
      metric,
      trend,
      rate: ratePerMinute,
      confidence,
      prediction: {
        nextValue,
        timeToThreshold
      }
    };
  }

  /**
   * Check if higher values are bad for a metric
   */
  private isHigherBad(metric: string): boolean {
    const higherBadMetrics = ['averageResponseTime', 'errorRate', 'memoryUsage', 'queueDepth'];
    return higherBadMetrics.includes(metric);
  }

  /**
   * Calculate variance of values
   */
  private calculateVariance(values: number[]): number {
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const squaredDiffs = values.map(value => Math.pow(value - mean, 2));
    return squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  }

  /**
   * Check for circuit breaker alerts
   */
  private checkCircuitBreakerAlerts(optimizerStatus: any): void {
    if (optimizerStatus.circuitBreakerState === 'OPEN') {
      this.createAlert({
        severity: AlertSeverity.CRITICAL,
        category: AlertCategory.AVAILABILITY,
        trigger: AlertTrigger.CIRCUIT_BREAKER_OPEN,
        title: 'Circuit Breaker Activated',
        description: 'Processing circuit breaker is open due to high failure rate',
        metric: 'circuitBreakerState',
        currentValue: 1,
        thresholdValue: 0,
        context: {
          circuitBreakerState: optimizerStatus.circuitBreakerState,
          optimizerStatus
        }
      });
    }
  }

  /**
   * Clean up expired data
   */
  private cleanupExpiredData(): void {
    const now = Date.now();
    const retentionTime = this.config.alertRetention;

    // Clean up resolved alerts from active alerts
    for (const [alertId, alert] of this.activeAlerts.entries()) {
      if (alert.resolved && alert.resolvedAt && (now - alert.resolvedAt) > 60000) {
        this.activeAlerts.delete(alertId);
      }
    }

    // Clean up old alert history
    this.alertHistory = this.alertHistory.filter(alert => 
      (now - alert.timestamp) < retentionTime
    );

    // Clean up expired suppressions
    for (const [key, endTime] of this.suppressedAlerts.entries()) {
      if (now > endTime) {
        this.suppressedAlerts.delete(key);
      }
    }

    // Clean up old sustained thresholds
    for (const [key, startTime] of this.sustainedThresholds.entries()) {
      if ((now - startTime) > 300000) { // 5 minutes max
        this.sustainedThresholds.delete(key);
      }
    }
  }

  /**
   * Get current alerts
   */
  public getActiveAlerts(): PerformanceAlert[] {
    return Array.from(this.activeAlerts.values());
  }

  /**
   * Get alert history
   */
  public getAlertHistory(limit?: number): PerformanceAlert[] {
    const sorted = this.alertHistory.sort((a, b) => b.timestamp - a.timestamp);
    return limit ? sorted.slice(0, limit) : sorted;
  }

  /**
   * Get alerting statistics
   */
  public getAlertingStats(): {
    activeAlerts: number;
    totalAlerts: number;
    alertsBySeverity: Record<AlertSeverity, number>;
    alertsByCategory: Record<AlertCategory, number>;
    acknowledgedAlerts: number;
    resolvedAlerts: number;
  } {
    const active = this.getActiveAlerts();
    const all = this.alertHistory;

    const alertsBySeverity = Object.values(AlertSeverity).reduce((acc, severity) => {
      acc[severity] = all.filter(a => a.severity === severity).length;
      return acc;
    }, {} as Record<AlertSeverity, number>);

    const alertsByCategory = Object.values(AlertCategory).reduce((acc, category) => {
      acc[category] = all.filter(a => a.category === category).length;
      return acc;
    }, {} as Record<AlertCategory, number>);

    return {
      activeAlerts: active.length,
      totalAlerts: all.length,
      alertsBySeverity,
      alertsByCategory,
      acknowledgedAlerts: all.filter(a => a.acknowledged).length,
      resolvedAlerts: all.filter(a => a.resolved).length
    };
  }

  /**
   * Add notification handler
   */
  public addNotificationHandler(handler: AlertNotificationHandler): void {
    this.config.notificationHandlers.push(handler);
    this.logger.info('Notification handler added', 'addNotificationHandler', {
      handlerName: handler.name,
      enabled: handler.enabled
    });
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<PerformanceAlertingConfig>): void {
    Object.assign(this.config, newConfig);
    this.logger.info('Alerting configuration updated', 'updateConfig');
  }
}

// Create singleton instance
export const performanceAlerting = new PerformanceAlertingService();

 